import {
  buildHtml,
  buildLink,
} from '@amazeelabs/react-framework-bridge/gatsby';
import { NewsPage, StandardLayout } from '@nuklear9/ui';
import { graphql, PageProps } from 'gatsby';
import React from 'react';

import { mapCoverBlockFragmentToCoverProps } from '../gatsby-node/fragments/CoverBlock';
import {
  mapTopicListItemFragmentsToMainTopics,
  mapTopicListItemFragmentsToSubTopics,
} from '../gatsby-node/fragments/TopicListItem';
import { LocationState, PageContext } from '../types';
import { buildContent, useLayoutProps, usePrefooterPath } from '../utils';
import { useFooterTagCloud } from '../utils/use-footer-tagcloud';

export const EventPageQuery = graphql`
  query EventPage($id: String!, $locale: String!) {
    drupalSettings {
      preFooterMembership {
        translations {
          langcode
          path
        }
      }
      preFooterNewsletterSubscription {
        translations {
          langcode
          path
        }
      }
      preFooterExternalNewsletterLink {
        langcode
        path
      }
    }
    page: drupalEvent(remoteId: { eq: $id }) {
      localizations: translations {
        langcode
        path
      }
      id
      ...Event
    }
  }
`;

const EventPageTemplate = ({
  data,
  pageContext,
  location,
}: PageProps<EventPageQuery, PageContext, LocationState>) => {
  const event = data.page;
  const cover = event?.cover;

  const footerTagCloud = useFooterTagCloud(pageContext.locale);

  const externalNewsletterLinkSettings = data.drupalSettings?.preFooterExternalNewsletterLink?.find(
    (item) => item?.langcode === pageContext.locale,
  );
  const newsletterLinkInternalPath = usePrefooterPath(
    pageContext.locale,
    data.drupalSettings?.preFooterNewsletterSubscription?.translations,
  );

  const layout = useLayoutProps(pageContext, location, {
    seo: {
      title: event?.title,
      imageUrl: cover?.image?.url,
      imageAlt: cover?.image?.translation
        ? cover.image.translation.alt
        : undefined,
      description: cover?.leadtext,
      og: {
        type: 'article',
      },
      meta: [
        {
          property: 'article:published_time',
          content: event?.meta.articlePublishedTime || '',
        },
      ].concat(
        event?.topics.map((item) => {
          return {
            property: 'article:tag',
            content: item.title,
          };
        }) || [],
      ),
    },
    preFooter: {
      NewsletterLink: buildLink(externalNewsletterLinkSettings ? {
        href: externalNewsletterLinkSettings.path
      } : {
        segments: [newsletterLinkInternalPath],
      }),
      MembershipLink: buildLink({
        segments: [
          usePrefooterPath(
            pageContext.locale,
            data.drupalSettings?.preFooterMembership?.translations,
          ),
        ],
      }),
    },
    footerTags: footerTagCloud?.length
      ? footerTagCloud.map((item) => ({
          id: item?.id || '',
          Link: buildLink({ href: item?.path || '/' }),
          title: item?.title || '',
        }))
      : [],
  });

  if (!event) {
    return null;
  }

  return (
    <StandardLayout {...layout} messages={location.state?.messages}>
      {cover && (
        <NewsPage
          tags={{
            tags: mapTopicListItemFragmentsToSubTopics(event?.topics),
          }}
          cover={{
            ...mapCoverBlockFragmentToCoverProps(cover),
            date: event?.publishedDate,
            topics: mapTopicListItemFragmentsToMainTopics(event?.topics),
          }}
          EventsProps={{
            startDate: event.startDate || '',
            endDate: event.endDate || '',
            Location: buildHtml(event.location || ''),
            AdditionalInformation: event.additionalInformation
              ? buildHtml(event.additionalInformation)
              : undefined,
          }}
        >
          {buildContent(event.body, pageContext)}
        </NewsPage>
      )}
    </StandardLayout>
  );
};

export default EventPageTemplate;
